import { useTranslation } from 'next-i18next';
import PropTypes from 'prop-types';
import React, { Fragment, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import * as Yup from 'yup';
import 'yup-phone-lite';

import { getErrorMessage } from '@utils/validation';
import request from '@utils/request';

import { formikShape } from '@common/types/formik';
import {
  EMAIL_CONFIRM_ERROR,
  INVALID_EMAIL_ERROR,
  REQUIRED_FIELD_ERROR,
  INVALID_PHONE_NUMBER,
} from '@common/constants/formValidation';
import { PHONE_PREFIX_TO_COUNTRY_MAP } from '@common/constants/countries';
import { BUTTON_VARIANTS } from '@common/components/Button';
import FilesUpload from '@common/components/FilesUpload';
import {
  SelectionControl,
  FormControl,
  PhoneNumberInput,
  RadioButton,
  RadiosWrapper,
  TextInput,
} from '@common/components/forms';
import {
  FILE_VALIDATION_INCOMPLETE_UPLOAD,
  FILE_VALIDATION_TYPE_ERROR,
  validateJobApplyFile,
} from '@common/components/JobApplyForm/JobApplyForm.helpers';
import LoadingIndicator from '@common/components/LoadingIndicator';
import { GENDER_FEMALE, GENDER_MALE, GENDERS } from '@common/constants/genders';
import {
  STEP_ALTERNATIVES,
  STEP_CONTACT_INFO,
  STEP_DOCUMENTS,
  STEP_SUBMITTED,
} from '@common/constants/jobs';
import * as endpoints from '@common/endpoints';
import HTMLRenderer from '@common/components/CMSComponents/HTMLRenderer';

import AlternativeJobsSection from './AlternativeJobsSection';
import {
  AcknowledgeFormControl,
  FilesSection,
  FormColumn,
  FormFooter,
  FormHr,
  FormRow,
  SectionDescription,
  SectionDisclaimer,
  SectionTitle,
  SubmitButton,
} from './JobApplyForm.styled';

const updateObjectInArray = (items, key, updates) =>
  items.map(item => {
    if (item.key !== key) {
      return item;
    }

    return { ...item, ...updates };
  });

const JobApplyForm = ({
  isApprenticeship,
  formik,
  hasAlternatives,
  alternativeOffers,
  isSpontaneous,
}) => {
  const { t } = useTranslation();
  const { values, touched, errors, handleChange, handleBlur, setFieldValue } =
    formik;
  const [files, setFiles] = useState([]);
  const validFiles = files.filter(f => f.isValid && !f.isLoading);

  let acknowledgeLabel;

  const uploadFile = (file, key) => {
    const options = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };
    const formData = new FormData();

    formData.append('file', file);

    return request
      .post(
        endpoints.jobsUploadApplicationDocument(values.uuid),
        formData,
        options,
      )
      .then(response => {
        setFiles(prev =>
          updateObjectInArray(prev, key, {
            id: response.data.id,
            isLoading: false,
          }),
        );
      })
      .catch(error => {
        const status = error?.response?.status;

        setFiles(prev =>
          updateObjectInArray(prev, key, {
            errors: [
              ...(file?.errors || []),
              status === 400
                ? FILE_VALIDATION_TYPE_ERROR
                : FILE_VALIDATION_INCOMPLETE_UPLOAD,
            ],
            isLoading: false,
            isValid: false,
          }),
        );
      });
  };

  const handleFileAdded = e => {
    const addedFiles = Array.from(e.target.files || []);

    if (!addedFiles.length) {
      return;
    }

    const parsedFiles = addedFiles.map(file => {
      const validationErrors = validateJobApplyFile(file);
      const isValid = !validationErrors.length;
      const parsedFile = {
        errors: validationErrors,
        id: null,
        isLoading: isValid,
        isValid,
        key: `${file.name}_${Date.now()}`,
        name: file.name,
        size: file.size,
        type: file.type,
      };

      if (isValid) {
        uploadFile(file, parsedFile.key);
      }

      return parsedFile;
    });

    setFiles(prevFiles => [...prevFiles, ...parsedFiles]);

    e.target.value = '';
  };

  const handleFileDelete = file => {
    if (file.isValid) {
      request
        .delete(endpoints.jobsDeleteApplicationDocument(values.uuid, file.id))
        .then(() => {
          setFiles(prevFiles => prevFiles.filter(f => f.key !== file.key));
        });

      return;
    }
    setFiles(prevFiles => prevFiles.filter(f => f.key !== file.key));
  };

  const isLastStep =
    values.step === STEP_ALTERNATIVES ||
    (values.step === STEP_DOCUMENTS && !hasAlternatives);

  const handleAlternativeOfferClicked = id => () => {
    const newOtherJobs = values.otherJobOffers.includes(id)
      ? values.otherJobOffers.filter(v => v !== id)
      : [...values.otherJobOffers, id];

    formik.setFieldValue('otherJobOffers', newOtherJobs);
  };

  const submitDisabled =
    formik.isSubmitting ||
    (values.step !== STEP_CONTACT_INFO && !values.acknowledgement) ||
    (values.step !== STEP_CONTACT_INFO && validFiles.length === 0);

  if (isApprenticeship) {
    acknowledgeLabel = t(
      'jobApplyForm.uploadSection.apprenticeshipAcknowledgeLabel',
      'In meinen Unterlagen sind enthalten: Bewerbungsschreiben, Lebenslauf, Schulzeugnisse & wenn vorhanden Multicheck / Stellwerk.',
    );
  } else if (!isApprenticeship || isSpontaneous) {
    acknowledgeLabel = t(
      'jobApplyForm.uploadSection.acknowledgeLabel',
      'In meinen Unterlagen sind enthalten: Bewerbungsschreiben, Lebenslauf, Arbeitszeugnisse & alle relevanten Diplome.',
    );
  }

  return (
    <>
      {values.step !== STEP_SUBMITTED && (
        <form
          onSubmit={formik.handleSubmit}
          noValidate
        >
          <SectionTitle>
            {t('jobApplyForm.infoSection.title', 'Persönliche Angaben')}
          </SectionTitle>
          <FormRow>
            <FormColumn>
              <FormControl
                label={t('jobApplyForm.genderLabel', 'Anrede')}
                labelAs="span"
                hasError={touched.gender && !!errors.gender}
                message={touched.gender && getErrorMessage(t, errors.gender)}
              >
                <RadiosWrapper>
                  <RadioButton
                    name="gender"
                    value={GENDER_FEMALE}
                    text={t('jobApplyForm.gender.femaleLabel', 'Frau')}
                    checked={values.gender === GENDER_FEMALE}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={formik.isSubmitting}
                  />
                  <RadioButton
                    name="gender"
                    value={GENDER_MALE}
                    text={t('jobApplyForm.gender.maleLabel', 'Herr')}
                    checked={values.gender === GENDER_MALE}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={formik.isSubmitting}
                  />
                </RadiosWrapper>
              </FormControl>
            </FormColumn>
          </FormRow>

          <FormRow>
            <FormColumn>
              <FormControl
                label={t('jobApplyForm.firstNameLabel', 'Vorname')}
                hasError={touched.firstName && !!errors.firstName}
                message={
                  touched.firstName && getErrorMessage(t, errors.firstName)
                }
              >
                <TextInput
                  name="firstName"
                  value={values.firstName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  hasError={touched.firstName && !!errors.firstName}
                  type="text"
                  placeholder={t('jobApplyForm.firstNamePlaceholder', '')}
                  disabled={formik.isSubmitting}
                />
              </FormControl>
            </FormColumn>

            <FormColumn>
              <FormControl
                label={t('jobApplyForm.lastNameLabel', 'Nachname')}
                hasError={touched.lastName && !!errors.lastName}
                message={
                  touched.lastName && getErrorMessage(t, errors.lastName)
                }
              >
                <TextInput
                  name="lastName"
                  value={values.lastName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  hasError={touched.lastName && !!errors.lastName}
                  type="text"
                  placeholder={t('jobApplyForm.lastNamePlaceholder', '')}
                  disabled={formik.isSubmitting}
                />
              </FormControl>
            </FormColumn>
          </FormRow>

          <FormRow>
            <FormColumn>
              <FormControl
                hasError={touched.email && !!errors.email}
                label={t('jobApplyForm.emailAddress', 'E-Mail Adresse')}
                message={touched.email && getErrorMessage(t, errors.email)}
              >
                <TextInput
                  name="email"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  hasError={touched.email && !!errors.email}
                  type="email"
                  inputMode="email"
                  placeholder={t(
                    'jobApplyForm.emailPlaceholder',
                    'doris.mustermann@mail.ch',
                  )}
                  disabled={formik.isSubmitting}
                />
              </FormControl>
            </FormColumn>

            <FormColumn>
              <FormControl
                hasError={touched.confirmEmail && !!errors.confirmEmail}
                label={t(
                  'jobApplyForm.confirmEmailAddress',
                  'E-Mail Adresse wiederholen',
                )}
                message={
                  touched.confirmEmail &&
                  getErrorMessage(t, errors.confirmEmail)
                }
              >
                <TextInput
                  name="confirmEmail"
                  value={values.confirmEmail}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  hasError={touched.confirmEmail && !!errors.confirmEmail}
                  type="email"
                  inputMode="email"
                  placeholder={t(
                    'jobApplyForm.confirmEmailPlaceholder',
                    'doris.mustermann@mail.ch',
                  )}
                  disabled={formik.isSubmitting}
                  autoComplete="off"
                />
              </FormControl>
            </FormColumn>
          </FormRow>

          <FormRow>
            <FormColumn>
              <FormControl
                hasError={touched.phoneNumber && !!errors.mobilePhone}
                message={
                  touched.phoneNumber && getErrorMessage(t, errors.mobilePhone)
                }
                label={t('jobApplyForm.mobilePhoneLabel', 'Mobile')}
                noSpacing
              >
                <PhoneNumberInput
                  name="mobilePhone"
                  value={values.mobilePhone}
                  onBlur={handleBlur}
                  setFieldValue={setFieldValue}
                  prefixFieldName="phonePrefix"
                  numberFieldName="phoneNumber"
                  prefixFieldId="phonePrefix"
                  numberFieldId="phoneNumber"
                  hasError={touched.phoneNumber && !!errors.mobilePhone}
                  disabled={formik.isSubmitting}
                  withSpacing
                />
              </FormControl>
            </FormColumn>
          </FormRow>

          <CSSTransition
            in={values.step !== STEP_CONTACT_INFO}
            classNames="transition"
            timeout={500}
            appear
            enter
            exit
            enterOnMount
          >
            <FilesSection>
              <SectionTitle>
                {t('jobApplyForm.uploadSection.title', 'Unterlagen hochladen')}
              </SectionTitle>
              <SectionDescription>
                <HTMLRenderer
                  customAs="span"
                  html={t(
                    'jobApplyForm.uploadSection.text',
                    'Bitte lade hier Deine vollständigen Bewerbungsunterlagen, vorzugsweise als PDF, hoch. (Maximal 10 MB pro Datei)',
                  )}
                />
              </SectionDescription>

              <SectionDisclaimer>
                <HTMLRenderer
                  customAs="span"
                  html={t(
                    'jobApplyForm.uploadSection.disclaimer',
                    'Bitte lade hier Deine vollständigen Bewerbungsunterlagen, vorzugsweise als PDF, hoch. (Maximal 10 MB pro Datei)',
                  )}
                />
              </SectionDisclaimer>

              <FilesUpload
                inputId="job-apply-files-input"
                files={files}
                onFilesAdded={handleFileAdded}
                onFileDelete={handleFileDelete}
                addFileButtonText={
                  files.length === 0
                    ? t(
                        'jobApplyForm.uploadSection.addFilesButtonText',
                        'Datei hochladen',
                      )
                    : t(
                        'jobApplyForm.uploadSection.addMoreFilesButtonText',
                        'Weitere Datei hinzufügen',
                      )
                }
              />

              <CSSTransition
                in={files.length > 0}
                classNames="transition"
                timeout={300}
                appear
                enter
                exit
                enterOnMount
              >
                <AcknowledgeFormControl noSpacing>
                  <SelectionControl
                    name="acknowledgement"
                    disabled={formik.isSubmitting}
                    checked={values.acknowledgement}
                    onChange={handleChange}
                    label={acknowledgeLabel}
                    variant="paragraph2"
                    alignment="beseline"
                  />
                </AcknowledgeFormControl>
              </CSSTransition>
            </FilesSection>
          </CSSTransition>

          <CSSTransition
            in={values.step === STEP_ALTERNATIVES}
            classNames="transition"
            timeout={500}
            appear
            enter
            exit
            enterOnMount
          >
            <AlternativeJobsSection
              handleAlternativeOfferClicked={handleAlternativeOfferClicked}
              alternativeOffers={alternativeOffers}
              otherJobOffers={values.otherJobOffers}
              isLast={isLastStep}
            />
          </CSSTransition>

          <FormHr isLastStep={values.step === STEP_ALTERNATIVES} />
          <FormFooter>
            <SubmitButton
              disabled={submitDisabled}
              iconRight={
                formik.isSubmitting && <LoadingIndicator color="#ffffff" />
              }
              variant={
                !formik.isSubmitting && submitDisabled
                  ? BUTTON_VARIANTS.LEVEL_2_BLUEGREY_90
                  : BUTTON_VARIANTS.LEVEL_1_GREEN
              }
            >
              {!isLastStep
                ? t('jobApplyForm.nextStepButtonText', 'Nächster Schritt')
                : t('jobApplyForm.submitButtonText', 'Bewerbung senden')}
            </SubmitButton>
          </FormFooter>
        </form>
      )}
    </>
  );
};

JobApplyForm.propTypes = {
  alternativeOffers: PropTypes.arrayOf(PropTypes.shape({})),
  formik: formikShape.isRequired,
  hasAlternatives: PropTypes.bool,
  isApprenticeship: PropTypes.bool.isRequired,
  isSpontaneous: PropTypes.bool,
};

JobApplyForm.defaultProps = {
  alternativeOffers: [],
  hasAlternatives: false,
  isSpontaneous: false,
};

JobApplyForm.validationSchema = Yup.object().shape({
  confirmEmail: Yup.string()
    .oneOf([Yup.ref('email')], EMAIL_CONFIRM_ERROR)
    .required(REQUIRED_FIELD_ERROR),
  email: Yup.string().email(INVALID_EMAIL_ERROR).required(REQUIRED_FIELD_ERROR),
  firstName: Yup.string().required(REQUIRED_FIELD_ERROR),
  gender: Yup.string()
    .transform(value => value.toString())
    .oneOf(GENDERS),
  lastName: Yup.string().required(REQUIRED_FIELD_ERROR),
  mobilePhone: Yup.string().when('phonePrefix', (phonePrefix, schema) =>
    schema
      .phone(PHONE_PREFIX_TO_COUNTRY_MAP[phonePrefix], INVALID_PHONE_NUMBER)
      .required(REQUIRED_FIELD_ERROR),
  ),
});

export default JobApplyForm;
